import { useState, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import {
  DndContext,
  closestCenter,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Pencil, Plus, Trash2 } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loading } from '@/components/Loading';
import { CredentialReviewStatus } from '@paladin-monorepo/common';
import {
  credentialItemStatusKey,
  credentialStatusBadgeVariant,
} from '@/lib/credentialItemStatus';
import { toast } from 'sonner';
import { caseStudiesApi } from '@/lib/api/case-studies';
import { CaseStudyForm } from '@/forms/CaseStudyForm';
import type {
  CaseStudyDraftFromFileDto,
  CaseStudyDto,
  CreateCaseStudyDto,
  UpdateCaseStudyDto,
} from '@paladin-monorepo/common';

export interface CaseStudyCreatePrefill {
  draft: CaseStudyDraftFromFileDto;
  pendingFile: File;
}

export interface CaseStudiesCardProps {
  createPrefill?: CaseStudyCreatePrefill | null;
  onCreatePrefillConsumed?: () => void;
}

export const CaseStudiesCard = ({
  createPrefill = null,
  onCreatePrefillConsumed,
}: CaseStudiesCardProps) => {
  const { t } = useTranslation();
  const [caseStudies, setCaseStudies] = useState<CaseStudyDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCaseStudy, setEditingCaseStudy] = useState<CaseStudyDto | null>(null);
  const [draftValues, setDraftValues] = useState<CaseStudyDraftFromFileDto | null>(null);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [formKey, setFormKey] = useState(0);

  const replacementsByOriginal = useMemo(() => {
    const map = new Map<string, CaseStudyDto>();
    for (const cs of caseStudies) {
      if (cs.replacesCaseStudyId) {
        map.set(cs.replacesCaseStudyId, cs);
      }
    }
    return map;
  }, [caseStudies]);

  const canonicalCaseStudies = useMemo(
    () => caseStudies.filter((cs) => !cs.replacesCaseStudyId),
    [caseStudies],
  );

  const createMutation = useMutation({
    mutationFn: (dto: CreateCaseStudyDto) =>
      caseStudiesApi.createCaseStudy(dto),
    onError: () => {
      toast.error(t('profile.caseStudies.add.submit.error.title'), {
        description: t('profile.caseStudies.add.submit.error.description'),
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateCaseStudyDto }) =>
      caseStudiesApi.updateCaseStudy(id, dto),
    onError: () => {
      toast.error(t('profile.caseStudies.edit.submit.error.title'), {
        description: t('profile.caseStudies.edit.submit.error.description'),
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => caseStudiesApi.deleteCaseStudy(id),
    onSuccess: (_, id) => {
      setCaseStudies((prev) =>
        prev.filter((cs) => cs.id !== id && cs.replacesCaseStudyId !== id),
      );
      toast.success(t('profile.caseStudies.delete.success'));
    },
    onError: () => {
      toast.error(t('profile.caseStudies.delete.error'));
    },
  });

  const handleFormSuccess = (result: CaseStudyDto, wasCreate: boolean) => {
    setCaseStudies((prev) => {
      const exists = prev.some((cs) => cs.id === result.id);
      if (exists) {
        return prev.map((cs) => (cs.id === result.id ? result : cs));
      }
      return [...prev, result].sort((a, b) => a.sortOrder - b.sortOrder);
    });
    setEditingCaseStudy(result);
    setDraftValues(null);
    setPendingFiles([]);
    onCreatePrefillConsumed?.();
    if (wasCreate) {
      toast.success(t('profile.caseStudies.add.submit.success.title'), {
        description: t('profile.caseStudies.attachments.addAfterCreate'),
      });
    } else {
      setDialogOpen(false);
      setEditingCaseStudy(null);
      toast.success(t('profile.caseStudies.edit.submit.success.title'));
    }
  };

  const fetchCaseStudies = useCallback(async () => {
    try {
      const data = await caseStudiesApi.getCaseStudies();
      setCaseStudies(data);
    } catch (error) {
      console.error('Failed to fetch case studies:', error);
      toast.error(t('offers.form.references.messages.failedToLoad'));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchCaseStudies();
  }, [fetchCaseStudies]);

  useEffect(() => {
    if (!createPrefill) return;
    setEditingCaseStudy(null);
    setDraftValues(createPrefill.draft);
    setPendingFiles([createPrefill.pendingFile]);
    setFormKey((k) => k + 1);
    setDialogOpen(true);
  }, [createPrefill]);

  const handleOpenCreate = () => {
    setEditingCaseStudy(null);
    setDraftValues(null);
    setPendingFiles([]);
    setFormKey((k) => k + 1);
    setDialogOpen(true);
  };

  const handleOpenEdit = (cs: CaseStudyDto) => {
    const replacement = replacementsByOriginal.get(cs.id);
    setEditingCaseStudy(replacement ?? cs);
    setDraftValues(null);
    setPendingFiles([]);
    setFormKey((k) => k + 1);
    setDialogOpen(true);
  };

  const handleDialogClose = (open: boolean) => {
    if (!open) {
      setEditingCaseStudy(null);
      setDraftValues(null);
      setPendingFiles([]);
      onCreatePrefillConsumed?.();
    }
    setDialogOpen(open);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = canonicalCaseStudies.map((cs) => cs.id);
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;
    const nextIds = arrayMove(ids, oldIndex, newIndex);
    setCaseStudies((prev) => {
      const byId = new Map(prev.map((cs) => [cs.id, cs]));
      const reordered = nextIds.map((id, index) => ({
        ...byId.get(id)!,
        sortOrder: index,
      }));
      const rest = prev.filter((cs) => !!cs.replacesCaseStudyId);
      return [...reordered, ...rest];
    });
    try {
      const next = await caseStudiesApi.reorder(nextIds);
      setCaseStudies(next);
    } catch {
      toast.error(t('profile.caseStudies.reorderError'));
      fetchCaseStudies();
    }
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>{t('profile.caseStudies.title')}</CardTitle>
            <CardDescription>
              {t('profile.caseStudies.description')}
            </CardDescription>
          </div>
          <Button onClick={handleOpenCreate} size="sm">
            <Plus className="h-4 w-4 mr-1" />
            {t('generic.add')}
          </Button>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {isLoading && <Loading text={t('generic.loading')} />}
            {!isLoading && canonicalCaseStudies.length > 0 && (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext
                  items={canonicalCaseStudies.map((cs) => cs.id)}
                  strategy={rectSortingStrategy}
                >
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {canonicalCaseStudies.map((cs) => (
                      <SortableCaseStudyCard
                        key={cs.id}
                        cs={cs}
                        replacement={replacementsByOriginal.get(cs.id)}
                        onEdit={() => handleOpenEdit(cs)}
                        onDelete={() =>
                          deleteMutation.mutate(
                            replacementsByOriginal.get(cs.id)?.id ?? cs.id,
                          )
                        }
                        isDeleting={deleteMutation.isPending}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
            {!isLoading && canonicalCaseStudies.length === 0 && (
              <p className="text-muted-foreground text-sm">
                {t('profile.caseStudies.empty')}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={handleDialogClose}>
        <DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingCaseStudy
                ? t('profile.caseStudies.edit.title')
                : t('profile.caseStudies.add.title')}
            </DialogTitle>
          </DialogHeader>
          <CaseStudyForm
            key={formKey}
            initialData={editingCaseStudy || undefined}
            draftValues={editingCaseStudy ? null : draftValues}
            initialPendingFiles={editingCaseStudy ? [] : pendingFiles}
            onSubmit={(data) =>
              editingCaseStudy
                ? updateMutation.mutateAsync({ id: editingCaseStudy.id, dto: data })
                : createMutation.mutateAsync(data as CreateCaseStudyDto)
            }
            onSuccess={(result) =>
              handleFormSuccess(result, !editingCaseStudy)
            }
            onCancel={() => setDialogOpen(false)}
            isSubmitting={createMutation.isPending || updateMutation.isPending}
            embedded
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

function SortableCaseStudyCard({
  cs,
  replacement,
  onEdit,
  onDelete,
  isDeleting,
}: {
  cs: CaseStudyDto;
  replacement?: CaseStudyDto;
  onEdit: () => void;
  onDelete: () => void;
  isDeleting: boolean;
}) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: cs.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  const liveStatus = credentialItemStatusKey(cs.reviewStatus, false);
  const pendingStatus = replacement
    ? credentialItemStatusKey(replacement.reviewStatus, true)
    : null;
  const display = replacement ?? cs;

  return (
    <div ref={setNodeRef} style={style} className="rounded-md border p-3 relative group">
      <button
        type="button"
        className="absolute top-2 left-2 h-7 w-7 cursor-grab text-muted-foreground hover:text-foreground active:cursor-grabbing"
        aria-label={t('profile.caseStudies.reorder')}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={onEdit}
          aria-label={t('generic.edit')}
        >
          <Pencil className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={onDelete}
          disabled={isDeleting}
          aria-label={t('generic.delete')}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
      <div className="font-medium pr-16 pl-7 flex flex-wrap items-center gap-2">
        {cs.title}
        <Badge variant={credentialStatusBadgeVariant(liveStatus)}>
          {t(`profile.caseStudies.reviewStatus.${liveStatus}`)}
        </Badge>
        {pendingStatus && (
          <Badge variant={credentialStatusBadgeVariant(pendingStatus)}>
            {t(`profile.caseStudies.reviewStatus.${pendingStatus}`)}
          </Badge>
        )}
      </div>
      {replacement && (
        <div className="text-xs text-muted-foreground pl-7 pt-1">
          {t('profile.caseStudies.pendingVersion')}
        </div>
      )}
      <div className="text-sm text-muted-foreground pl-7">
        {cs.industry?.name}
      </div>
      <div className="text-sm text-muted-foreground pl-7">
        {cs.expertises?.map((e) => e.name).join(', ')}
      </div>
      <div className="text-sm text-muted-foreground pl-7">
        {cs.authors?.map((a) => `${a.firstName} ${a.lastName}`).join(', ')}
      </div>
      <div className="text-sm text-muted-foreground pt-4 pl-7">
        {display.description}
      </div>
      {display.reviewStatus === CredentialReviewStatus.Rejected &&
        display.rejectionReason && (
          <div className="text-destructive text-xs mt-2 pl-7">
            {display.rejectionReason}
          </div>
        )}
    </div>
  );
}
